// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { StyledButton } from "@insite/mobius/Clickable";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { constants } from "@stanion/XDL/constants";
import { avoidBreakInsidePrintElement, color, xClassNames, xScreen } from "@stanion/XDL/Utilities";
import { getBreakpoint } from "@stanion/XDL/Utilities/breakpointMediaQueries";
import { mergeWithCss } from "@stanion/XDL/Utilities/mergeWithCss";
import React, { Children, FC, useState } from "react";
import { css } from "styled-components";

export interface CartLineContainerStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    imageInfoWrapper?: InjectableCss;
    image?: InjectableCss;
    infoSection?: InjectableCss;
    qtyPriceSection?: InjectableCss;
    qty?: InjectableCss;
    price?: InjectableCss;
    total?: InjectableCss;
    removeAction?: InjectableCss;
    peripheralActionsTop?: InjectableCss;
    peripheralActionsBottom?: InjectableCss;
}

export const cartLineContainerStyles: CartLineContainerStyles = {
    container: {
        css: css`
            display: flex;
            padding: 22px 0 24px 12px;
            column-gap: 12px;
            &.first-item {
                padding-top: 16px;
            }
            &:not(.first-item) {
                border-top: 1px solid ${color("common.border")};
            }

            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                &,
                & > * {
                    display: none;
                }
            }
            @media print {
                display: none;
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            position: relative;
            flex: 1;
        `,
    },
    imageInfoWrapper: {
        css: css`
            display: flex;
            gap: 16px;
            ${xScreen("lte", "1200")} {
                ${({ isCondensed }: any) => css`
                    ${isCondensed
                        ? css`
                              width: 316px;
                          `
                        : css`
                              width: 260px;
                          `};
                `}
            }
            ${({ isFullWidth }: any) => css`
                ${isFullWidth
                    ? css`
                          flex: 1;
                      `
                    : css`
                          width: 340px;
                      `};
            `}
        `,
    },
    image: {
        css: css`
            width: 88px;
            height: 88px;
            position: relative;
        `,
    },
    infoSection: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${({ isCondensed, isFullWidth }: any) => css`
                ${isFullWidth
                    ? css`
                          flex: 1;
                          max-width: 630px;
                      `
                    : isCondensed
                    ? css`
                          max-width: 385px;
                          ${xScreen("lte", "1200")} {
                              max-width: 300px;
                          }
                      `
                    : css`
                          max-width: 316px;
                          ${xScreen("lte", "1200")} {
                              max-width: 250px;
                          }
                      `};
            `}
        `,
    },
    qtyPriceSection: {
        css: css`
            display: flex;
            flex-grow: 1;

            ${({ isCondensed, isFullWidth }: any) => css`
                ${isFullWidth &&
                css`
                    min-width: 470px;
                    flex-grow: 0;
                `};

                ${isFullWidth &&
                isCondensed &&
                css`
                    max-width: 56%;
                `};
            `}
        `,
    },
    price: {
        css: css`
            flex: 1;
            max-width: 167px;
            padding-top: 4px;
            ${xScreen("gt", constants.cartLine.breakpoints.md )}{
                > section {
                    max-width: 143px;
                }
            }
        `,
    },
    qty: {
        css: css`
            flex: 1;
            max-width: 167px;
        `,
    },
    total: {
        css: css`
            flex: 1;
            padding-top: 8px;
        `,
    },
    peripheralActionsTop: {
        css: css`
            &,
            & > span {
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex: 1;
                align-items: flex-start;
                ${xScreen("lte", "1200")} {
                    gap: 60px;
                }
            }
        `,
    },
    removeAction: {
        css: css`
            ${StyledButton} {
                margin-right: 12px;
            }
        `,
    },
    peripheralActionsBottom: {
        css: css`
            width: 100%;
        `,
    },
};

export interface MobileCartLineContainerStyles {
    container?: InjectableCss;
    cardContainer?: InjectableCss;
    wrapper?: InjectableCss;
    image?: InjectableCss;
    upperSection?: InjectableCss;
    mainWrapper?: InjectableCss;
    removeAction?: InjectableCss;
    total?: InjectableCss;
    qty?: InjectableCss;
    lowerSection?: InjectableCss;
    qtyPriceSection?: InjectableCss;
    peripheralActions?: InjectableCss;
    peripheralActionsTop?: InjectableCss;
    peripheralActionsBottom?: InjectableCss;
}

export const mobileCartLineContainerStyles: MobileCartLineContainerStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 16px 0;
            &.first-item {
                padding-top: 7px;
            }
            &:not(.first-item) {
                border-top: 1px solid ${color("common.border")};
            }
            ${xScreen("gt", getBreakpoint(constants.cartLine.breakpoints.md, "next"))} {
                &,
                & > * {
                    display: none;
                }
            }

            @media print {
                display: block;
            }
            ${avoidBreakInsidePrintElement}
        `,
    },
    cardContainer: {
        css: css`
            display: flex;
            padding: 0 12px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            @media print {
                display: block;
            }
            ${avoidBreakInsidePrintElement}
            width: 100%;
        `,
    },
    upperSection: {
        css: css`
            display: flex;
            gap: 12px;
            max-width: 650px;
        `,
    },
    image: {
        css: css`
            width: 88px;
            height: 88px;
            position: relative;
        `,
    },
    mainWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            margin-right: 12px;
            @media print {
                display: block;
            }
        `,
    },
    removeAction: {
        css: css`
            ${StyledButton} {
                align-items: baseline;
            }
        `,
    },
    lowerSection: {
        css: css`
            margin-top: 12px;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            ${xScreen("lte", "md")} {
                margin-top: 25px;
            }
            @media print {
                display: block;
            }
        `,
    },
    qty: {
        css: css`
            line-height: 0;
            max-width: 120px;
            margin-right: 90px;
            ${xScreen("lte", "xs")} {
                margin-right: 56px;
            }
        `,
    },
    qtyPriceSection: {
        css: css`
            margin-top: 16px;
            display: flex;
            flex-grow: 1;
        `,
    },
    peripheralActions: {
        css: css`
            margin-top: 16px;
            display: flex;
            flex-direction: column;
        `,
    },
    peripheralActionsTop: {
        css: css`
            & > span {
                display: flex;
                justify-content: space-between;
            }
        `,
    },
};

const Image: FC = () => null;
const Info: FC = () => null;
const PeripheralActionsTop: FC = () => null;
const PeripheralActionsBottom: FC = () => null;
const Qty: FC = () => null;
const Price: FC = () => null;
const Total: FC = () => null;
const RemoveAction: FC = () => null;

const CartLineContainer = ({
    children,
    extendedStyles,
    mobileExtendedStyles,
    orderLineStyle,
    ...otherProps
}: {
    children: Array<React.ReactElement>;
    extendedStyles?: CartLineContainerStyles;
    mobileExtendedStyles?: MobileCartLineContainerStyles;
    orderLineStyle?: boolean;
    [key: string]: any;
}) => {
    const [styles] = useState(() => mergeWithCss(cartLineContainerStyles, extendedStyles));
    const [mobileStyles] = useState(() => mergeWithCss(mobileCartLineContainerStyles, mobileExtendedStyles));

    const childrenArray = Children.toArray(children) as unknown as React.ReactElement[];
    const imageSlot = childrenArray.find(child => child.type === Image);
    const infoSlot = childrenArray.find(child => child.type === Info);
    const peripheralActionsTop = childrenArray.find(child => child.type === PeripheralActionsTop);
    const peripheralActionsBottom = childrenArray.find(child => child.type === PeripheralActionsBottom);
    const qtySlot = childrenArray.find(child => child.type === Qty);
    const priceSlot = childrenArray.find(child => child.type === Price);
    const totalSlot = childrenArray.find(child => child.type === Total);
    const removeActionSlot = childrenArray.find(child => child.type === RemoveAction);

    const orderLineStyleClassName = orderLineStyle ? "orderLineStyle" : "";
    const className = xClassNames.append(orderLineStyleClassName, otherProps.className);
    const mobileClassName = xClassNames.append(className, "mobile-line");

    const totalQtyOrdered =
        qtySlot && qtySlot.props?.children ? qtySlot.props?.children.props?.cart?.totalQtyOrdered : 0;
    const qtyCharactersLength = `${totalQtyOrdered}`.length;

    return (
        <>
            {/* Mobile styles */}
            <StyledWrapper {...mobileStyles.container} {...otherProps} className={mobileClassName}>
                <StyledWrapper {...mobileStyles.cardContainer}>
                    <StyledWrapper {...mobileStyles.wrapper}>
                        <StyledWrapper {...mobileStyles.upperSection}>
                            {imageSlot && (
                                <StyledWrapper {...mobileStyles.image}>{imageSlot?.props?.children}</StyledWrapper>
                            )}
                            <StyledWrapper {...mobileStyles.mainWrapper}>
                                {infoSlot?.props.children || <div style={{ lineHeight: 0 }}></div>}
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...mobileStyles.lowerSection}>
                            {priceSlot?.props.children || <div style={{ lineHeight: 0 }}></div>}
                            <StyledWrapper {...mobileStyles.qtyPriceSection}>
                                <StyledWrapper {...mobileStyles.qty} {...{ expandWidth: qtyCharactersLength > 4 }}>
                                    {qtySlot?.props.children || <div style={{ lineHeight: 0 }}></div>}
                                </StyledWrapper>
                                <StyledWrapper {...mobileStyles.total}>
                                    {totalSlot?.props.children || <div style={{ lineHeight: 0 }}></div>}
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...mobileStyles.peripheralActions} data-test-selector="peripheralActions">
                                <StyledWrapper
                                    {...mobileStyles.peripheralActionsTop}
                                    data-test-selector="peripheralActionsTop"
                                >
                                    {peripheralActionsTop?.props?.children ? (
                                        <span style={{ lineHeight: 0 }}>
                                            {peripheralActionsTop?.props?.children || <></>}
                                        </span>
                                    ) : (
                                        <div style={{ lineHeight: 0 }}></div>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...mobileStyles.removeAction}>
                        {removeActionSlot?.props.children || <div style={{ lineHeight: 0 }}></div>}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...mobileStyles.peripheralActionsBottom} data-test-selector="peripheralActionsBottom">
                    {peripheralActionsBottom?.props?.children ? (
                        <span>{peripheralActionsBottom?.props?.children || <></>}</span>
                    ) : (
                        <div style={{ lineHeight: 0 }}></div>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            {/* Desktop styles */}
            <StyledWrapper {...styles.container} {...otherProps} className={className}>
                {imageSlot && <StyledWrapper {...styles.image}>{imageSlot?.props?.children}</StyledWrapper>}
                <StyledWrapper {...styles.wrapper}>
                    <StyledWrapper {...styles.imageInfoWrapper} {...otherProps}>
                        <StyledWrapper {...styles.infoSection} className={orderLineStyleClassName} {...otherProps}>
                            {infoSlot?.props.children}
                            <StyledWrapper {...styles.peripheralActionsTop} data-test-selector="peripheralActionsTop">
                                {peripheralActionsTop?.props?.children}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.qtyPriceSection} {...otherProps}>
                        <StyledWrapper {...styles.price}>{priceSlot?.props.children}</StyledWrapper>
                        <StyledWrapper {...styles.qty} {...{ expandWidth: qtyCharactersLength > 4 }}>
                            {qtySlot?.props.children}
                        </StyledWrapper>
                        <StyledWrapper {...styles.total}>{totalSlot?.props.children}</StyledWrapper>
                        <StyledWrapper {...styles.removeAction}>{removeActionSlot?.props.children}</StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.peripheralActionsBottom} data-test-selector="peripheralActionsBottom">
                        {peripheralActionsBottom?.props?.children || <></>}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

CartLineContainer.Image = Image;
CartLineContainer.Info = Info;
CartLineContainer.Qty = Qty;
CartLineContainer.Price = Price;
CartLineContainer.Total = Total;
CartLineContainer.RemoveAction = RemoveAction;
CartLineContainer.PeripheralActionsTop = PeripheralActionsTop;
CartLineContainer.PeripheralActionsBottom = PeripheralActionsBottom;

export interface LineHeaderStyles {
    wrapper?: InjectableCss;
    itemDescription?: InjectableCss;
    rightSection?: InjectableCss;
    qty?: InjectableCss;
    priceUnit?: InjectableCss;
    total?: InjectableCss;
}

export const lineHeaderStyles: LineHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            background: ${color("primary")};
            color: ${color("common.ui1")};
            font-size: 12px;
            line-height: 20px;
            font-weight: bold;
            width: 100%;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                display: none;
            }
        `,
    },
    itemDescription: {
        css: css`
            width: 440px;
            padding: 8px 12px 12px;
            ${({ isCondensed, isFullWidth }: any) => css`
                ${isFullWidth
                    ? css`
                          flex: 1;
                      `
                    : isCondensed
                    ? css`
                          ${xScreen("lte", "1200")} {
                              max-width: 320px;
                          }
                      `
                    : css`
                          ${xScreen("lte", "1200")} {
                              max-width: 366px;
                          }
                      `};
            `}
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                width: 100%;
                max-width: 100%;
                & > ${TypographyStyle} {
                    display: none;
                }
            }
        `,
    },
    rightSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            ${({ isFullWidth }: any) => css`
                ${isFullWidth &&
                css`
                    min-width: 490px;
                    flex: 0;
                `};
            `}
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                display: none;
            }
        `,
    },
    priceUnit: {
        css: css`
            flex: 1;
            padding: 8px 12px;
            max-width: 167px;
        `,
    },
    qty: {
        css: css`
            flex: 1;
            padding: 8px 12px;
            max-width: 167px;
        `,
    },
    total: {
        css: css`
            flex: 1;
            padding: 8px 12px;
        `,
    },
};

interface LinesHeaderProps {
    leftTitle?: string;
    rightTitles?: string[];
    extendedStyles?: LineHeaderStyles;
    orderLineStyle?: boolean;
    isCondensed?: boolean;
    isFullWidth?: boolean;
}

export const LinesHeader: FC<LinesHeaderProps> = ({
    leftTitle,
    rightTitles,
    orderLineStyle,
    extendedStyles,
    isCondensed,
    isFullWidth,
}) => {
    const [styles] = useState(() => mergeWithCss(lineHeaderStyles, extendedStyles));
    const orderLineStyleClassName = orderLineStyle ? "orderLineStyle" : "";

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper
                {...styles.itemDescription}
                className={orderLineStyleClassName}
                {...{ isCondensed, isFullWidth }}
            >
                <Typography>{translate(leftTitle || "")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection} {...{ isFullWidth }}>
                <StyledWrapper {...styles.priceUnit}>{translate(rightTitles?.[0] || "")}</StyledWrapper>
                <StyledWrapper {...styles.qty}>{translate(rightTitles?.[1] || "")}</StyledWrapper>
                {rightTitles?.[2] && (
                    <StyledWrapper {...styles.total}>{translate(rightTitles?.[2] || "")}</StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

LinesHeader.defaultProps = {
    leftTitle: "Item Description",
    rightTitles: ["Price/Unit", "Qty", "Total"],
    isCondensed: false,
};

export default CartLineContainer;
